import { Ref, ref } from "vue";
import { BuilderEstimatorSteps } from "@/enums/BuilderEstimatorSteps";
import { StyleOption } from "@/types/BuilderEstimatorStyleOption";
import { IBuilderEstimatorFormData } from "@/types/BuilderEstimatorFormData";
import { HouseDesignInterface } from "@/types/HouseDesignInterface";
import { FacadeInterface } from "@/types/FacadeInterface";
import {
  ExternalColorSchemeInterface,
  InternalColorSchemeInterface,
} from "@/types/ColorSchemeInterface";
import { HouseInclusionsPack } from "@/types/HouseInclusionPack";
import { OptionalUpgrade } from "@/types/OptionalUpgradeInterface";

type UseBuilderEstimatorSelectionsReturn = {
  formData: Ref<IBuilderEstimatorFormData>;
  onSelectOptionStyle: (
    option: StyleOption,
    currentStep: FormDataSteps
  ) => void;
  onSelectLandDetails: (landDetails: {
    width: number | string;
    length: number | string;
  }) => void;
  onSelectHouseDesign: (houseDesign: { houseDesignId: string }) => void;
  onSelectInclusionPack: (inclusionPack: HouseInclusionsPack | null) => void;
  onSelectOptionalUpgrade: (
    upgrade: OptionalUpgrade,
    isSelected: boolean
  ) => void;
};

export type FormDataSteps = keyof IBuilderEstimatorFormData;

export const useBuilderEstimatorSelections = (
  builderId: string | null,
  houses: Ref<HouseDesignInterface[] | null>,
  onSelectNext: () => void
) => {
  const userSelections = ref({
    [BuilderEstimatorSteps.LAND_DETAILS]: {
      landWidth: null,
      landDepth: null,
    },
    [BuilderEstimatorSteps.SELECT_DESIGN]: {
      houseBuilderId: builderId,
      houseDesignId: null,
      houseDesign: null,
    },
    [BuilderEstimatorSteps.SELECT_FACADE]: {
      facade: null,
    },
    [BuilderEstimatorSteps.SELECT_EXTERNAL_COLOURS]: {
      externalColorScheme: null,
    },
    [BuilderEstimatorSteps.SELECT_INTERNAL_COLOURS]: {
      internalColorScheme: null,
    },
    [BuilderEstimatorSteps.SELECT_INCLUSIONS]: {
      inclusionPack: null,
    },
    [BuilderEstimatorSteps.OPTIONAL_UPGRADES]: {
      optionalUpgrades: null,
    },
    [BuilderEstimatorSteps.SUMMARY]: {
      summary: null,
    },
  } as IBuilderEstimatorFormData);

  const setStepSelection = <T extends FormDataSteps>(
    step: T,
    value: IBuilderEstimatorFormData[T]
  ) => {
    userSelections.value[step] = value;
  };

  const onSelectOptionStyle = (
    option: StyleOption,
    currentStep: FormDataSteps
  ) => {
    const styleUpdates = new Map([
      [
        BuilderEstimatorSteps.SELECT_FACADE,
        () =>
          setStepSelection(currentStep, { facade: option as FacadeInterface }),
      ],
      [
        BuilderEstimatorSteps.SELECT_EXTERNAL_COLOURS,
        () =>
          setStepSelection(currentStep, {
            externalColorScheme: option as ExternalColorSchemeInterface,
          }),
      ],
      [
        BuilderEstimatorSteps.SELECT_INTERNAL_COLOURS,
        () =>
          setStepSelection(currentStep, {
            internalColorScheme: option as InternalColorSchemeInterface,
          }),
      ],
    ]);

    styleUpdates.get(currentStep)?.();
  };

  const onSelectLandDetails = (landDetails: {
    width: number | string;
    length: number | string;
  }) => {
    setStepSelection(BuilderEstimatorSteps.LAND_DETAILS, {
      landWidth: landDetails.width,
      landDepth: landDetails.length,
    });
  };

  const onSelectInclusionPack = (inclusionPack: HouseInclusionsPack | null) => {
    setStepSelection(BuilderEstimatorSteps.SELECT_INCLUSIONS, {
      inclusionPack,
    });
  };

  const onSelectHouseDesign = ($event: { houseDesignId: string | null }) => {
    const house =
      houses.value?.find((house) => house._id === $event.houseDesignId) ?? null;

    if (!house) {
      setStepSelection(BuilderEstimatorSteps.SELECT_DESIGN, {
        houseBuilderId: builderId,
        houseDesignId: null,
        houseDesign: null,
      });
      setStepSelection(BuilderEstimatorSteps.SELECT_FACADE, { facade: null });
      return;
    }

    setStepSelection(BuilderEstimatorSteps.SELECT_DESIGN, {
      houseBuilderId: builderId,
      houseDesignId: $event.houseDesignId,
      houseDesign: house,
    });
    setStepSelection(BuilderEstimatorSteps.SELECT_FACADE, { facade: null });
  };
  const onViewDesignSelect = (event: { houseDesignId: string }) => {
    onSelectHouseDesign(event);
    onSelectNext();
  };

  const onSelectOptionalUpgrade = (
    upgrade: OptionalUpgrade,
    isSelected: boolean
  ) => {
    const currentUpgrades =
      userSelections.value[BuilderEstimatorSteps.OPTIONAL_UPGRADES]
        .optionalUpgrades || [];

    const updateUpgrades = (upgrades: OptionalUpgrade[] | null) => {
      setStepSelection(BuilderEstimatorSteps.OPTIONAL_UPGRADES, {
        optionalUpgrades: upgrades,
      });
    };

    if (isSelected) {
      const hasUpgrade = currentUpgrades.some((u) => u._id === upgrade._id);

      if (!hasUpgrade) {
        updateUpgrades([...currentUpgrades, upgrade]);
      }

      return;
    }

    const remainingUpgrades = currentUpgrades.filter(
      (u) => u._id !== upgrade._id
    );

    updateUpgrades(remainingUpgrades.length ? remainingUpgrades : null);
  };

  return {
    formData: userSelections,
    onSelectOptionStyle,
    onSelectInclusionPack,
    onSelectLandDetails,
    onSelectHouseDesign,
    onViewDesignSelect,
    onSelectOptionalUpgrade,
  };
};
